import type { UniversalIssueWithScreenshot } from '@deque/axe-devtools-issues-utils';
import React, { useMemo, useState, useEffect } from 'react';
import {
  ColumnHeader,
  ColumnLeft,
  ColumnRight,
  TwoColumnPanel
} from '@deque/cauldron-react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type ProductSlugs } from '@deque/billing-utils';

import type { OrNull } from '../../../../typings/utils';
import useSearchParams from '../../hooks/useSearchParams';
import Issues, { type GetIssueURL } from './Issues';
import ZeroStatePanel from './ZeroStatePanel';
import styles from './IssuesPanel.css';

export interface IssuesPanelProps {
  issues: UniversalIssueWithScreenshot[];
  issuesProductSourceSlug: ProductSlugs;
  getIssueURL?: GetIssueURL;
  onShare?: () => Promise<void>;
  noScreenshotExplanation?: React.ReactNode;
  onIssueView?: (currentIssue: UniversalIssueWithScreenshot) => void;
  onIntegrationShare?: () => void;
  testId?: string;
}

interface Rule {
  ruleId: string;
  issues: UniversalIssueWithScreenshot[];
  ruleName: string;
}

const getRules = (issues: UniversalIssueWithScreenshot[]): Rule[] => {
  const grouped = issues.reduce((acc, issue) => {
    if (!acc[issue.ruleId]) {
      acc[issue.ruleId] = [];
    }
    acc[issue.ruleId].push(issue);
    return acc;
  }, {} as Record<string, UniversalIssueWithScreenshot[]>);
  return Object.keys(grouped).map(key => ({
    ruleId: key,
    issues: grouped[key],
    ruleName: grouped[key][0].help
  }));
};

const IssuesPanel: React.FC<IssuesPanelProps> = ({
  issues,
  getIssueURL,
  onShare,
  issuesProductSourceSlug,
  noScreenshotExplanation,
  onIssueView,
  onIntegrationShare,
  testId = ''
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { pathname, hash } = history.location;
  const searchParams = useSearchParams();
  const rules: Rule[] = useMemo(() => getRules(issues), [issues]);

  // Get the selected rule from the URL, or default to the first rule
  const [selectedRule, setSelectedRule] = useState<OrNull<Rule>>(
    rules.length === 0
      ? null
      : searchParams.get('ruleId')
      ? rules.find(rule => rule.ruleId === searchParams.get('ruleId')) ??
        rules[0]
      : rules[0]
  );

  // Get the issue index from the URL, or default to 1
  const issueIndex =
    searchParams.get('issueIndex') &&
    !isNaN(Number(searchParams.get('issueIndex')))
      ? Number(searchParams.get('issueIndex'))
      : 1;

  const setIssueURL = (
    newIssueIndex: number,
    newRuleId: string,
    replace = true
  ) => {
    searchParams.set('issueIndex', newIssueIndex.toString());
    searchParams.set('ruleId', newRuleId);
    const url = `${pathname}?${new URLSearchParams(
      searchParams
    ).toString()}${hash}`;
    if (replace) {
      history.replace(url);
    } else {
      history.push(url);
    }
  };

  useEffect(() => {
    // If the issue index is invalid, reset to 1
    if (
      selectedRule &&
      searchParams.get('issueIndex') &&
      isNaN(Number(searchParams.get('issueIndex')))
    ) {
      setIssueURL(1, selectedRule.ruleId);
    }

    // If the rule ID is invalid, reset to the first rule
    if (
      searchParams.get('ruleId') &&
      !rules.find(rule => rule.ruleId === searchParams.get('ruleId'))
    ) {
      setIssueURL(1, rules[0].ruleId);
    }
  }, []);

  useEffect(() => {
    if (
      selectedRule &&
      (!searchParams.get('issueIndex') || !searchParams.get('ruleId'))
    ) {
      setIssueURL(1, selectedRule.ruleId);
    }
  }, [selectedRule]);

  if (!selectedRule) {
    return (
      <ZeroStatePanel>
        {t('There are 0 issues based on your current filter criteria.')}
      </ZeroStatePanel>
    );
  }

  return (
    <TwoColumnPanel className={styles.wrap}>
      <ColumnLeft aria-labelledby="issues-panel-header">
        <ColumnHeader id="issues-panel-header">
          <h2>{t('Issues ({{count}})', { count: issues.length })}</h2>
        </ColumnHeader>
        <nav aria-label={t('Issue navigation')}>
          <ul>
            {rules.map(rule => (
              <li key={rule.ruleId}>
                <a
                  href="#"
                  onClick={() => {
                    setSelectedRule(rule);
                    setIssueURL(1, rule.ruleId, false);
                  }}
                  aria-current={rule.ruleId === selectedRule?.ruleId}
                >
                  {rule.ruleName}
                  <span className={styles.ruleIssueCount}>
                    {rule.issues.length === 1
                      ? t('1 issue')
                      : t('{{count}} issues', { count: rule.issues.length })}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </ColumnLeft>
      <ColumnRight aria-labelledby="rule-header">
        <ColumnHeader id="rule-header">
          <h2>{selectedRule.ruleName}</h2>
        </ColumnHeader>
        {/* Issues is wrapped in a fragment to account for how TwoColumnPanel overrides
         * the key value of its children. See: https://github.com/dequelabs/cauldron/issues/924 */}
        <>
          <Issues
            key={selectedRule.ruleId}
            issues={selectedRule.issues}
            testId={testId}
            issuesProductSourceSlug={issuesProductSourceSlug}
            onIssueView={onIssueView}
            onShare={onShare}
            initialPage={issueIndex}
            getIssueURL={getIssueURL}
            noScreenshotExplanation={noScreenshotExplanation}
            onIntegrationShare={onIntegrationShare}
          />
        </>
      </ColumnRight>
    </TwoColumnPanel>
  );
};

export default IssuesPanel;
